/**
 * Admin catalog-suggestions API layer — PIM Wave P3 Task 4. Typed calls to
 * the SHIPPED PIM Wave P3 Task 2 `/admin/catalog/suggestions*` routes
 * (`apps/api/garden/features/catalog_schema/suggestions_router.py`), all
 * gated `marketplace.suggestion.review` (granted content_moderator +
 * super_admin). This task adds NO backend routes.
 *
 * Types mirror `apps/api/garden/features/catalog_schema/schemas.py:601-714`
 * (`SuggestionResponse`, `SuggestionListResponse`, `SuggestionCountsResponse`,
 * `SuggestionApproveRequest`, `SuggestionApproveResponse`,
 * `SuggestionDeclineRequest`).
 */
import { apiClient } from "@/lib/api-client";
import type { GPillTone } from "@garden/ui";

export type SuggestionKind = "category" | "field" | "option";

/**
 * The raw JSONB a seller submitted (mirrors the fields `SuggestionCreate`
 * persists into `CatalogSuggestion.payload`, `catalog_schema/schemas.py:604-627`).
 */
export interface SuggestionPayload {
  name: string;
  note?: string | null;
  parent_category_id?: string | null;
  field_type?: string | null;
}

/** One `catalog_suggestions` row (mirrors `SuggestionResponse`). */
export interface SuggestionResponse {
  id: string;
  seller_id: string;
  kind: SuggestionKind;
  payload: SuggestionPayload;
  /** `pending` | `approved` | `declined`. */
  status: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  decline_reason: string | null;
  created_at: string;
}

/** `GET /admin/catalog/suggestions` (mirrors `SuggestionListResponse`). */
export interface SuggestionListResponse {
  suggestions: SuggestionResponse[];
  next_cursor: string | null;
}

/** `GET /admin/catalog/suggestions/counts` (mirrors `SuggestionCountsResponse`). */
export interface SuggestionCounts {
  pending: number;
  approved: number;
  declined: number;
}

/**
 * Body for `POST /admin/catalog/suggestions/{id}/approve` (mirrors
 * `SuggestionApproveRequest`). Every field is optional and used ONLY for
 * `kind="category"` (silently ignored otherwise). When a category field is
 * omitted, the service falls back to the submitted payload's hint
 * (`name` -> `name_ru`, `parent_category_id` -> `parent_id`) — EXCEPT
 * `slug`, which has NO payload fallback: a category approval without a
 * non-empty `slug` 422s (`pim.suggestion_slug_required`).
 */
export interface SuggestionApproveRequest {
  slug?: string;
  name_ru?: string;
  name_ky?: string;
  name_en?: string;
  parent_id?: string;
}

/**
 * Response for a successful approve (mirrors `SuggestionApproveResponse`).
 * `created_category_id` is set only for `kind="category"`. `payload` is
 * echoed back so the field/option handoff can read the proposed name/type.
 */
export interface SuggestionApproveResponse {
  suggestion: SuggestionResponse;
  created_category_id: string | null;
  kind: SuggestionKind;
  payload: SuggestionPayload;
}

export interface ListSuggestionsParams {
  /** A value in `pending|approved|declined` — the bucket being viewed. */
  status: string;
  cursor?: string | null;
  limit?: number;
}

/**
 * `GET /admin/catalog/suggestions?status=...&order=asc&cursor=&limit=50` —
 * always oldest-first (FIFO) for the review queue.
 */
export function listSuggestions(
  params: ListSuggestionsParams,
): Promise<SuggestionListResponse> {
  const query = new URLSearchParams();
  query.set("status", params.status);
  query.set("order", "asc");
  if (params.cursor) query.set("cursor", params.cursor);
  query.set("limit", String(params.limit ?? 50));
  return apiClient.get<SuggestionListResponse>(
    `/admin/catalog/suggestions?${query.toString()}`,
  );
}

export function getSuggestionCounts(): Promise<SuggestionCounts> {
  return apiClient.get<SuggestionCounts>("/admin/catalog/suggestions/counts");
}

/**
 * Idempotent — safe to retry. `body` is empty (`{}`) for `kind="field"`/
 * `"option"` — the fields are silently ignored server-side for those kinds.
 */
export function approveSuggestion(
  id: string,
  body: SuggestionApproveRequest,
): Promise<SuggestionApproveResponse> {
  return apiClient.post<SuggestionApproveResponse, SuggestionApproveRequest>(
    `/admin/catalog/suggestions/${id}/approve`,
    body,
  );
}

/**
 * Idempotent — safe to retry. `reason` is REQUIRED (min 5, max 500 chars —
 * mirrors `SuggestionDeclineRequest`) and is shown to the seller.
 */
export function declineSuggestion(
  id: string,
  reason: string,
): Promise<SuggestionResponse> {
  return apiClient.post<SuggestionResponse, { reason: string }>(
    `/admin/catalog/suggestions/${id}/decline`,
    { reason },
  );
}

export const suggestionKeys = {
  /** `["admin","suggestions","list",status]`. */
  list: (status: string) => ["admin", "suggestions", "list", status] as const,
  /** `["admin","suggestions","counts"]`. */
  counts: () => ["admin", "suggestions", "counts"] as const,
} as const;

// ─────────────────────────── Tone helpers ──────────────────────────────────

/** `kind` -> `GPill` tone (category/field/option each get a distinct look). */
export function suggestionKindTone(kind: SuggestionKind): GPillTone {
  switch (kind) {
    case "category":
      return "primary";
    case "field":
      return "accent";
    case "option":
      return "neutral";
  }
}

/** `status` -> `GPill` tone. Anything other than approved/declined (i.e.
 * `pending`) reads as `warn` — an open item awaiting a decision. */
export function suggestionStatusTone(status: string): GPillTone {
  switch (status) {
    case "approved":
      return "success";
    case "declined":
      return "error";
    default:
      return "warn";
  }
}
